import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const formatDate = (date) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat('en-GB', options).format(new Date(date));
};

const NeckSizeGraph = ({ type, showLine, height = '300px', width = '100%', measurements }) => {
    const [labels, setLabels] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentValue, setCurrentValue] = useState(null);
    const [unitValue, setUnitValue] = useState(null);
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    useEffect(() => {
        if (measurements && measurements.length > 0) {
            const filteredData = measurements.filter(m => m.type === type);
            if (filteredData.length > 0) {
                const reversedData = [...filteredData].reverse();
                
                setLabels(reversedData.map(item => formatDate(item.recordedAt)));
                setData(reversedData.map(item => parseFloat(item.value)));

                setCurrentValue(parseFloat(reversedData[0]?.value));
                setUnitValue(reversedData[0]?.unit);
            } else {
                setLabels([]);
                setData([]);
                setCurrentValue(null);
                setUnitValue(null);
            }
        }
        setLoading(false);
    }, [measurements]);  // 🔹 Now graph updates whenever `measurements` change

    if (loading) {
        return (
            <Box textAlign="center" py={2}>
                <CircularProgress />
                <Typography variant="body2" mt={1}>Loading Graph...</Typography>
            </Box>
        );
    }

    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const minRange = data.length === 1 ? Math.floor(minValue * 2) / 2 - 2 : minValue - 0.5;
    const maxRange = data.length === 1 ? Math.ceil(maxValue * 2) / 2 + 2 : maxValue + 0.5;

    const chartOptionsWithLine = {
        responsive: true,
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        scales: {
            x: {
                display: true,
                type: 'category',
                labels,
                title: { 
                    display: true, 
                    text: 'Date',
                    color: isDarkMode ? '#fff' : '#666'
                },
                grid: {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    color: isDarkMode ? '#aaa' : '#666',
                }
            },
            y: {
                display: true,
                beginAtZero: false,
                min: minRange,
                max: maxRange,
                title: { 
                    display: true, 
                    text: `${type}`,
                    color: isDarkMode ? '#fff' : '#666'
                },
                grid: {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
                ticks: { 
                    stepSize: (maxValue - minValue) / 10,
                    color: isDarkMode ? '#aaa' : '#666',
                },
            },
        },
    };

    const chartOptionsWithoutLine = {
        responsive: true,
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        scales: {
            x: { display: false, labels, title: { display: true, text: 'Date' } },
            y: { display: false, min: minRange, max: maxRange, stepSize: 2, title: { display: true, text: `${type}` } },
        },
    };

    const chartData = {
        labels,
        datasets: [
            {
                label: `${type} Measurements`,
                data,
                fill: true,
                borderColor: isDarkMode ? '#90caf9' : '#3c50b4',
                backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.2)' : 'rgba(92, 107, 192, 0.1)',
                pointBackgroundColor: isDarkMode ? '#90caf9' : '#3c50b4',
                pointBorderColor: isDarkMode ? '#1e1e1e' : '#fff',
                pointRadius: 4,
                tension: 0.4,
                pointHoverRadius: 6,
            },
        ],
    };

    const chartOptions = showLine ? chartOptionsWithLine : chartOptionsWithoutLine;

    
    const formatTypeMessage = (type) => {
        const formattedType = type
            .replace(/([A-Z])/g, ' $1')  
            .toLowerCase()               
            .replace(/^./, (str) => str.toUpperCase());  
        return formattedType;
    };

    return (
        <Box width={width} height={height} mx="auto" maxWidth="800px" sx={{ borderRadius: 2 }}>
            {showLine && (
                <Box flex="1">
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        {formatTypeMessage(type)} : 
                        <strong style={{ color: '#4caf50', fontSize: "34px" }}>
                            {currentValue || ""}
                        </strong>
                        <span style={{ fontSize: '14px', color: isDarkMode ? '#aaa' : '#757575' }}> {unitValue}</span>
                    </Typography>
                </Box>
            )}
            <Line data={chartData} options={chartOptions} />
        </Box>
    );
};

export default NeckSizeGraph;


